import React, { FC, useCallback } from 'react';

import { Icon, RotateIcon, VisuallyHidden } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import useOnClickOutside from 'hooks/use-on-click-outside';
import { useTabNavigationWatcher } from 'state/accessibility/use-tab-navigation-watcher';
import { ENABLE_LOYALTY_2_0_SEE_MORE_OPTIONS } from 'utils/constants';
import { isButtonLikeKeypress } from 'utils/event';

import { RewardsStyleVariant } from '../../types';
import {
  CategoryDropdownContainer,
  OptionsContainer,
  SeeMoreOptionsCTA,
  Title,
  TitleContainer,
} from '../styled';
import { CategoryDropdownProps } from '../types';

// TODO: consolidate with other solutions (TextExpander, SelectionContainer) in the code base, extract fieldset
export const CategoryDropdown: FC<CategoryDropdownProps> = ({
  children,
  focusOptionsWhenNotVisible = false,
  id,
  label,
  onClickSeeMoreActions,
  optionsVisible,
  overlay = false,
  selectedOptionName,
  title,
  toggleOptionsVisible,
  variant,
  handleOutsideClick,
  bold = false,
  ...props
}) => {
  const { isUsingTabNavigation } = useTabNavigationWatcher();
  const OutsideClickWrapper = useOnClickOutside(() => {
    if (handleOutsideClick) {
      handleOutsideClick();
    }
  });

  const { formatMessage } = useIntl();

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isButtonLikeKeypress(e)) {
        e.preventDefault();
        toggleOptionsVisible();
      }
    },
    [toggleOptionsVisible]
  );

  const showSeeMoreOptions =
    ENABLE_LOYALTY_2_0_SEE_MORE_OPTIONS && onClickSeeMoreActions && optionsVisible;

  return (
    <OutsideClickWrapper>
      <CategoryDropdownContainer
        optionsVisible={optionsVisible}
        overlay={overlay}
        variant={variant}
      >
        <TitleContainer>
          <Title
            aria-controls={`${id}-content`}
            aria-expanded={optionsVisible}
            isUsingTabNavigation={isUsingTabNavigation}
            onClick={toggleOptionsVisible}
            onKeyDown={onKeyDown}
            showBorder={optionsVisible}
            tabIndex={0}
            variant={variant}
            bold={bold}
          >
            <VisuallyHidden>
              {formatMessage(
                { id: 'rewardCategory' },
                {
                  categoryName: label,
                  selectedOptionText: selectedOptionName
                    ? formatMessage(
                        { id: 'containsSelectedOption' },
                        {
                          rewardTier: selectedOptionName,
                        }
                      )
                    : '',
                }
              )}
            </VisuallyHidden>
            <span aria-hidden>{title}</span>
            <RotateIcon active={optionsVisible}>
              <Icon
                icon="select"
                color={variant === RewardsStyleVariant.Secondary ? 'icon-reversed' : 'icon-default'}
                width="24px"
                aria-hidden
              />
            </RotateIcon>
          </Title>
        </TitleContainer>
        <OptionsContainer
          {...props}
          id={`${id}-content`}
          focusOptionsWhenNotVisible={focusOptionsWhenNotVisible}
          optionsVisible={optionsVisible}
          overlay={overlay}
          variant={variant}
        >
          {children}
          {showSeeMoreOptions && (
            <SeeMoreOptionsCTA onClick={onClickSeeMoreActions} variant={variant}>
              {formatMessage({ id: 'seeMoreOptions' })}
            </SeeMoreOptionsCTA>
          )}
        </OptionsContainer>
      </CategoryDropdownContainer>
    </OutsideClickWrapper>
  );
};
